import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import {
    fetchAgentStatus,
    fetchAgentPorts,
    fetchAgentDevicesAll,
    fetchAgentScanners,
    testAgentScanner,
    scanAgentNetwork,
    getVenueEquipment,
    listOrganizations,
    createOrganization,
    updateOrganization,
    verifyInn,
    linkOrgVenue,
    createKkm,
    testKkm,
    getFnStatus,
    createAcquiring,
    testAcquiring
} from '../services/equipmentApi';
import { useSchoolCashDeskEnabled } from '../hooks/useSchoolCashDeskEnabled';
import SkudVenueSettings from '../components/skud/SkudVenueSettings';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const emptyOrg = () => ({
    legalType: 'ooo',
    fullName: '',
    shortName: '',
    displayName: '',
    inn: '',
    kpp: '',
    ogrn: '',
    taxSystem: 'usn_income',
    legalAddress: '',
    paymentAddress: '',
    email: '',
    phone: '',
    ofdName: i18n.t('venueEquipment:defaultOfd')
});

const emptyKkm = (venueId) => ({
    venueId,
    name: '',
    brand: 'atol',
    modelName: '',
    serialNumber: '',
    fnsRegNumber: '',
    fnsRegDate: '',
    ffdVersion: '1.2',
    isForServices: true,
    fnModel: i18n.t('venueEquipment:defaultFnModel'),
    fnSerialNumber: '',
    fnValidityMonths: 36,
    fnActivationDate: '',
    fnExpirationDate: '',
    connectionMode: 'cloud',
    comPort: '',
    comBaudRate: 115200,
    tcpIpAddress: '',
    tcpPort: 7778,
    cloudLogin: '',
    cloudPassword: '',
    cloudGroupCode: '',
    cloudProvider: 'atol_online'
});

function addMonths(dateStr, months) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() + (months || 0));
    return d.toISOString().slice(0, 10);
}

/** Убирает пустые даты и лишние поля — иначе API возвращает 400 при автономной ККМ */
function buildKkmPayload(form, venueId) {
    const dto = {
        venueId,
        name: form.name?.trim(),
        brand: form.brand,
        modelName: form.modelName?.trim() || form.name?.trim() || i18n.t('venueEquipment:defaultKkmName'),
        serialNumber: form.serialNumber?.trim(),
        ffdVersion: form.ffdVersion || '1.2',
        isForServices: form.isForServices !== false,
        isForInternet: !!form.isForInternet,
        connectionMode: form.connectionMode || 'cloud',
        fnValidityMonths: form.fnValidityMonths || null
    };
    const optDate = (v) => (v && String(v).trim() ? v : undefined);
    const opt = (k, v) => {
        if (v != null && String(v).trim() !== '') dto[k] = v;
    };
    opt('fnsRegNumber', form.fnsRegNumber);
    opt('fnsRegDate', optDate(form.fnsRegDate));
    opt('fnModel', form.fnModel);
    opt('fnSerialNumber', form.fnSerialNumber);
    opt('fnActivationDate', optDate(form.fnActivationDate));
    const exp = optDate(form.fnExpirationDate) || addMonths(form.fnActivationDate, form.fnValidityMonths);
    opt('fnExpirationDate', exp);
    opt('comPort', form.comPort);
    if (form.comBaudRate) dto.comBaudRate = form.comBaudRate;
    opt('tcpIpAddress', form.tcpIpAddress);
    if (form.tcpPort) dto.tcpPort = form.tcpPort;
    opt('cloudLogin', form.cloudLogin);
    opt('cloudPassword', form.cloudPassword);
    opt('cloudGroupCode', form.cloudGroupCode);
    opt('cloudProvider', form.cloudProvider);
    return dto;
}

function daysUntil(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    return Math.ceil((d - now) / (86400000));
}

export default function VenueEquipmentPage() {
    const { t } = useTranslation(['venueEquipment', 'common', 'nav', 'messages', 'errors']);
    const LEGAL_TYPES = useMemo(() => [
        { v: 'ooo', l: t('venueEquipment:legalOoo') },
        { v: 'ip', l: t('venueEquipment:legalIp') },
        { v: 'ao', l: t('venueEquipment:legalAo') },
        { v: 'other', l: t('venueEquipment:legalOther') }
    ], [t]);
    const TAX_SYSTEMS = useMemo(() => [
        { v: 'usn_income', l: t('venueEquipment:taxUsnIncome') },
        { v: 'usn_income_outcome', l: t('venueEquipment:taxUsnIncomeOutcome') },
        { v: 'osn', l: t('venueEquipment:taxOsn') },
        { v: 'patent', l: t('venueEquipment:taxPatent') },
        { v: 'npd', l: t('venueEquipment:taxNpd') }
    ], [t]);
    const navigate = useNavigate();
    const { enabled: cashDeskEnabled } = useSchoolCashDeskEnabled();
    const { id: venueIdParam } = useParams();
    const venueId = parseInt(venueIdParam, 10);
    const [equipment, setEquipment] = useState(null);
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [agent, setAgent] = useState({ alive: false });
    const [showOrgForm, setShowOrgForm] = useState(false);
    const [orgForm, setOrgForm] = useState(emptyOrg());
    const [editOrgId, setEditOrgId] = useState(null);
    const [kkmModal, setKkmModal] = useState(null);
    const [kkmStep, setKkmStep] = useState(1);
    const [kkmForm, setKkmForm] = useState(emptyKkm(venueId));
    const [terminalModal, setTerminalModal] = useState(null);
    const [terminalForm, setTerminalForm] = useState({
        venueId,
        name: '',
        bank: 'sber',
        tid: '',
        mid: '',
        terminalModel: '',
        integrationType: 'standalone',
        connectionMode: 'standalone',
        kkmDeviceId: null,
        acceptsVisa: true,
        acceptsMastercard: true,
        acceptsMir: true,
        acceptsNfc: true
    });
    const [ports, setPorts] = useState([]);
    const [discovered, setDiscovered] = useState(null);
    const [scannerTest, setScannerTest] = useState('');
    const [busy, setBusy] = useState('');

    async function reload() {
        if (!venueId || Number.isNaN(venueId)) return;
        setLoading(true);
        setError('');
        try {
            const [eqRes, orgRes, agentStatus] = await Promise.all([
                getVenueEquipment(venueId),
                listOrganizations(),
                fetchAgentStatus()
            ]);
            setEquipment(eqRes.data);
            setOrgs(orgRes.data || []);
            setAgent(agentStatus);
        } catch (e) {
            setError(e.response?.data?.message || t('venueEquipment:errLoadEquipment'));
        }
        setLoading(false);
    }

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!venueId || Number.isNaN(venueId)) return;
            setLoading(true);
            setError('');
            try {
                const [eqRes, orgRes, agentStatus] = await Promise.all([
                    getVenueEquipment(venueId),
                    listOrganizations(),
                    fetchAgentStatus()
                ]);
                if (cancelled) return;
                setEquipment(eqRes.data);
                setOrgs(orgRes.data || []);
                setAgent(agentStatus);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.message || t('venueEquipment:errLoadEquipment'));
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [venueId]);

    const venueOrgs = useMemo(() => {
        if (!equipment?.organizations?.length) {
            return orgs.map(o => ({ organization: o, kkms: [], terminals: [] }));
        }
        return equipment.organizations;
    }, [equipment, orgs]);

    async function handleSaveOrg(e) {
        e.preventDefault();
        setBusy('org');
        setError('');
        const dto = {
            legalType: orgForm.legalType,
            fullName: orgForm.fullName,
            shortName: orgForm.shortName || null,
            displayName: orgForm.displayName || orgForm.fullName,
            inn: orgForm.inn.replace(/\D/g, ''),
            kpp: orgForm.legalType === 'ip' ? null : (orgForm.kpp || null),
            ogrn: orgForm.ogrn.replace(/\D/g, ''),
            taxSystem: orgForm.taxSystem,
            legalAddress: orgForm.legalAddress,
            paymentAddress: orgForm.paymentAddress || orgForm.legalAddress,
            email: orgForm.email,
            phone: orgForm.phone,
            ofdName: orgForm.ofdName
        };
        try {
            let orgId = editOrgId;
            if (editOrgId) {
                await updateOrganization(editOrgId, dto);
            } else {
                const res = await createOrganization(dto);
                orgId = res.data.id;
                await linkOrgVenue(orgId, venueId, { isDefault: true, sellsCategories: '["ice_time","rental"]' });
            }
            setShowOrgForm(false);
            setEditOrgId(null);
            setOrgForm(emptyOrg());
            await reload();
        } catch (err) {
            setError(err.response?.data?.message || t('venueEquipment:errSaveOrg'));
        }
        setBusy('');
    }

    async function handleVerifyInn(orgId, inn) {
        try {
            const res = await verifyInn(orgId, inn);
            const d = res.data;
            if (d.found) {
                alert(t('venueEquipment:innFound', { fullName: d.fullName, address: d.address || '', ogrn: d.ogrn || '—', status: d.status || '—' }));
            } else {
                alert(t('venueEquipment:innNotFound'));
            }
        } catch {
            alert(t('venueEquipment:errInnCheck'));
        }
    }

    async function loadPorts() {
        try {
            setPorts(await fetchAgentPorts());
        } catch {
            setError(t('venueEquipment:errComPorts'));
        }
    }

    async function handleSaveKkm(e) {
        e.preventDefault();
        if (!kkmModal) return;
        if (!kkmForm.name?.trim() || !kkmForm.serialNumber?.trim()) {
            setError(t('venueEquipment:errKkmStep1'));
            return;
        }
        setBusy('kkm');
        setError('');
        const dto = buildKkmPayload(kkmForm, venueId);
        try {
            await createKkm(kkmModal.orgId, dto);
            setKkmModal(null);
            setKkmStep(1);
            await reload();
        } catch (err) {
            const status = err.response?.status;
            const msg = err.response?.data?.message
                || err.response?.data?.title
                || (status === 403 ? t('venueEquipment:errNoPermission') : null)
                || (status === 400 ? t('venueEquipment:errBadForm') : null)
                || t('venueEquipment:errSaveKkm');
            setError(msg);
        }
        setBusy('');
    }

    async function handleTestKkm(orgId, kkmId) {
        setBusy(`test-${kkmId}`);
        try {
            const res = await testKkm(orgId, kkmId);
            alert(res.data.success ? t('venueEquipment:linkOk') : (res.data.message || t('venueEquipment:linkError')));
            await reload();
        } catch {
            alert(t('venueEquipment:testNotDone'));
        }
        setBusy('');
    }

    async function handleSaveTerminal(e) {
        e.preventDefault();
        if (!terminalModal) return;
        setBusy('term');
        try {
            await createAcquiring(terminalModal.orgId, {
                venueId,
                name: terminalForm.name,
                bank: terminalForm.bank,
                tid: terminalForm.tid,
                mid: terminalForm.mid,
                terminalModel: terminalForm.terminalModel,
                integrationType: terminalForm.integrationType,
                connectionMode: terminalForm.connectionMode,
                kkmDeviceId: terminalForm.kkmDeviceId,
                acceptsVisa: terminalForm.acceptsVisa,
                acceptsMastercard: terminalForm.acceptsMastercard,
                acceptsMir: terminalForm.acceptsMir,
                acceptsNfc: terminalForm.acceptsNfc
            });
            setTerminalModal(null);
            await reload();
        } catch (err) {
            setError(err.response?.data?.message || t('venueEquipment:errSaveTerminal'));
        }
        setBusy('');
    }

    const cardStyle = {
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        background: '#fff'
    };

    return (
        <OwnerPageLayout
            title={t('venueEquipment:title')}
            subtitle={equipment?.venueName ? t('venueEquipment:venueSubtitle', { name: equipment.venueName }) : undefined}
            maxWidth={960}
            actions={
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => navigate('/school/venues')} className="sp-btn sp-btn--ghost">
                        {t('venueEquipment:backVenues')}
                    </button>
                    {cashDeskEnabled && (
                        <Link to="/cashier" className="sp-btn sp-btn--ghost">{t('venueEquipment:cashierDesk')}</Link>
                    )}
                </div>
            }
        >
                <div style={{ ...cardStyle, marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>{agent.alive ? t('venueEquipment:agentOn') : t('venueEquipment:agentOff')}</span>
                    {agent.alive && <span style={{ color: '#64748b', fontSize: 13 }}>v{agent.version} · {agent.hostname}</span>}
                    {!agent.alive && (
                        <span style={{ fontSize: 13 }}>
                            <a href="/downloads/sportprogress-agent-setup.exe" style={{ marginRight: 12 }}>Windows</a>
                            <a href="/downloads/sportprogress-agent.service">Linux (systemd)</a>
                        </span>
                    )}
                    <button type="button" onClick={async () => setAgent(await fetchAgentStatus())}>{t('venueEquipment:checkAgent')}</button>
                </div>

                {agent.alive && (
                    <div style={{ ...cardStyle, marginTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>{t('venueEquipment:connectedEquipment')}</h3>
                            <button type="button" disabled={busy === 'discover'} onClick={async () => {
                                setBusy('discover');
                                try {
                                    setDiscovered(await fetchAgentDevicesAll());
                                } catch {
                                    setError(t('venueEquipment:errDeviceList'));
                                }
                                setBusy('');
                            }}>{t('venueEquipment:refreshList')}</button>
                        </div>
                        {discovered && (
                            <div style={{ marginTop: 12, fontSize: 14 }}>
                                <p><strong>{t('venueEquipment:comPorts')}</strong></p>
                                <ul style={{ margin: '4px 0 12px' }}>
                                    {(discovered.ports || []).map(p => (
                                        <li key={p.port}>✓ {p.port} — {p.description}</li>
                                    ))}
                                    {!(discovered.ports?.length) && <li>—</li>}
                                </ul>
                                <p><strong>{t('venueEquipment:usbDevices')}</strong></p>
                                <ul style={{ margin: '4px 0 12px' }}>
                                    {(discovered.usbDevices || []).slice(0, 15).map((u, i) => (
                                        <li key={i}>✓ {u.name}</li>
                                    ))}
                                </ul>
                                <p><strong>{t('venueEquipment:barcodeScanners')}</strong></p>
                                {(discovered.scanners || []).map(s => (
                                    <div key={s.deviceId} style={{ marginBottom: 8, padding: 8, background: '#f8fafc', borderRadius: 8 }}>
                                        ✓ {s.name}<br />
                                        <span style={{ color: '#64748b', fontSize: 12 }}>VID:{s.vendorId} PID:{s.productId}</span>
                                        <button type="button" style={{ marginLeft: 8 }} onClick={async () => {
                                            setScannerTest(t('venueEquipment:scannerPrompt'));
                                            const res = await testAgentScanner(s.deviceId);
                                            setScannerTest(res.success ? t('venueEquipment:scannerRead', { barcode: res.barcode }) : (res.errorText || t('common:error')));
                                        }}>{t('venueEquipment:testBarcode')}</button>
                                    </div>
                                ))}
                                {scannerTest && <p style={{ marginTop: 8 }}>{scannerTest}</p>}
                            </div>
                        )}
                    </div>
                )}

                {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
                {loading && <p>{t('common:loading')}</p>}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
                    <h2 style={{ margin: 0, fontSize: 18 }}>{t('venueEquipment:myOrgs')}</h2>
                    <button type="button" onClick={() => { setShowOrgForm(true); setEditOrgId(null); setOrgForm(emptyOrg()); }}>
                        {t('venueEquipment:addOrg')}
                    </button>
                </div>

                {showOrgForm && (
                    <form onSubmit={handleSaveOrg} style={{ ...cardStyle, background: '#f8fafc' }}>
                        <h3>{editOrgId ? t('common:edit') : t('venueEquipment:newOrg')}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label>{t('venueEquipment:labelType')}
                                <select value={orgForm.legalType} onChange={e => setOrgForm({ ...orgForm, legalType: e.target.value })}>
                                    {LEGAL_TYPES.map(lt => <option key={lt.v} value={lt.v}>{lt.l}</option>)}
                                </select>
                            </label>
                            <label>{t('venueEquipment:labelTax')}
                                <select value={orgForm.taxSystem} onChange={e => setOrgForm({ ...orgForm, taxSystem: e.target.value })}>
                                    {TAX_SYSTEMS.map(ts => <option key={ts.v} value={ts.v}>{ts.l}</option>)}
                                </select>
                            </label>
                            <label style={{ gridColumn: '1 / -1' }}>{t('venueEquipment:fullName')}
                                <input value={orgForm.fullName} onChange={e => setOrgForm({ ...orgForm, fullName: e.target.value })} required />
                            </label>
                            <label>{t('venueEquipment:displayName')}
                                <input value={orgForm.displayName} onChange={e => setOrgForm({ ...orgForm, displayName: e.target.value })} />
                            </label>
                            <label>{t('venueEquipment:inn')}
                                <input value={orgForm.inn} onChange={e => setOrgForm({ ...orgForm, inn: e.target.value })} required />
                            </label>
                            {orgForm.legalType !== 'ip' && (
                                <label>{t('venueEquipment:kpp')}
                                    <input value={orgForm.kpp} onChange={e => setOrgForm({ ...orgForm, kpp: e.target.value })} />
                                </label>
                            )}
                            <label>{t('venueEquipment:ogrn')}
                                <input value={orgForm.ogrn} onChange={e => setOrgForm({ ...orgForm, ogrn: e.target.value })} required />
                            </label>
                            <label style={{ gridColumn: '1 / -1' }}>{t('venueEquipment:legalAddress')}
                                <input value={orgForm.legalAddress} onChange={e => setOrgForm({ ...orgForm, legalAddress: e.target.value })} />
                            </label>
                        </div>
                        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                            <button type="submit" disabled={busy === 'org'}>{busy === 'org' ? '…' : t('common:save')}</button>
                            <button type="button" onClick={() => setShowOrgForm(false)}>{t('common:cancel')}</button>
                        </div>
                    </form>
                )}

                {venueOrgs.map(({ organization: org, kkms, terminals }) => (
                    <div key={org.id} style={cardStyle}>
                        <h3 style={{ margin: '0 0 8px' }}>{org.displayName || org.fullName}</h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: 14 }}>
                            {t('venueEquipment:orgInn', { inn: org.inn })}{org.kpp ? t('venueEquipment:orgKpp', { kpp: org.kpp }) : ''}{t('venueEquipment:orgOgrn', { ogrn: org.ogrn })}
                            <br />
                            {TAX_SYSTEMS.find(ts => ts.v === org.taxSystem)?.l || org.taxSystem}
                            {org.ofdName ? t('venueEquipment:orgOfd', { ofd: org.ofdName }) : ''}
                        </p>
                        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => {
                                setEditOrgId(org.id);
                                setOrgForm({
                                    legalType: org.legalType || 'ooo',
                                    fullName: org.fullName,
                                    shortName: org.shortName || '',
                                    displayName: org.displayName,
                                    inn: org.inn,
                                    kpp: org.kpp || '',
                                    ogrn: org.ogrn,
                                    taxSystem: org.taxSystem,
                                    legalAddress: org.legalAddress || '',
                                    paymentAddress: org.paymentAddress || '',
                                    email: org.email || '',
                                    phone: org.phone || '',
                                    ofdName: org.ofdName || ''
                                });
                                setShowOrgForm(true);
                            }}>{t('common:edit')}</button>
                            <button type="button" onClick={() => handleVerifyInn(org.id, org.inn)}>{t('venueEquipment:verifyInnFns')}</button>
                        </div>

                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{t('venueEquipment:kkms')}</strong>
                            <button type="button" onClick={() => {
                                setKkmModal({ orgId: org.id });
                                setKkmStep(1);
                                setKkmForm(emptyKkm(venueId));
                            }}>+ {t('common:add')}</button>
                        </div>
                        {(kkms || []).map(k => {
                            const days = daysUntil(k.fnExpirationDate);
                            return (
                                <div key={k.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginTop: 8 }}>
                                    <div>🖨 {k.name} · {k.modelName}</div>
                                    <div style={{ fontSize: 13, color: '#64748b' }}>
                                        {t('venueEquipment:kkmSerialLine', { serial: k.serialNumber })}
                                        {k.fnsRegNumber ? t('venueEquipment:fnsRegLine', { num: k.fnsRegNumber }) : ''}
                                        {k.fnExpirationDate ? t('venueEquipment:fnUntil', { date: k.fnExpirationDate.slice(0, 10) }) : ''}
                                    </div>
                                    {days != null && days <= 30 && (
                                        <div style={{ color: days <= 0 ? '#b91c1c' : '#d97706', fontSize: 13 }}>
                                            {days <= 0 ? t('venueEquipment:fnExpired') : t('venueEquipment:fnExpiresIn', { days })}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 13 }}>{t('venueEquipment:connectionLine', { mode: k.connectionMode, port: k.comPort ? ` (${k.comPort})` : '' })}</div>
                                    <button type="button" style={{ marginTop: 8 }} disabled={busy === `test-${k.id}`}
                                        onClick={() => handleTestKkm(org.id, k.id)}>{t('venueEquipment:testLink')}</button>
                                </div>
                            );
                        })}

                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{t('venueEquipment:terminals')}</strong>
                            <button type="button" onClick={() => {
                                setTerminalModal({ orgId: org.id });
                                setTerminalForm(f => ({ ...f, venueId, name: '', kkmDeviceId: kkms?.[0]?.id || null }));
                            }}>+ {t('common:add')}</button>
                        </div>
                        {(terminals || []).map(trm => (
                            <div key={trm.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginTop: 8 }}>
                                <div>💳 {trm.name} · {trm.bankDisplayName || trm.bank}</div>
                                <div style={{ fontSize: 13, color: '#64748b' }}>
                                    {trm.tid ? `TID: ${trm.tid}` : ''} {trm.terminalModel ? `· ${trm.terminalModel}` : ''}
                                </div>
                                <div style={{ fontSize: 13 }}>{trm.integrationType} · {trm.connectionMode}</div>
                            </div>
                        ))}
                    </div>
                ))}

                {!loading && venueOrgs.length === 0 && (
                    <p style={{ color: '#64748b' }}>{t('venueEquipment:noOrgs')}</p>
                )}

            {kkmModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <form onSubmit={handleSaveKkm} style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 520, width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
                        <h3>{t('venueEquipment:kkmStepTitle', { step: kkmStep })}</h3>
                        {kkmStep === 1 && (
                            <>
                                <label>{t('venueEquipment:name')}<input value={kkmForm.name} onChange={e => setKkmForm({ ...kkmForm, name: e.target.value })} required /></label>
                                <label>{t('venueEquipment:brand')}
                                    <select value={kkmForm.brand} onChange={e => setKkmForm({ ...kkmForm, brand: e.target.value })}>
                                        <option value="atol">{t('venueEquipment:brandAtol')}</option>
                                        <option value="shtrikh">{t('venueEquipment:brandShtrikh')}</option>
                                        <option value="evotor">{t('venueEquipment:brandEvotor')}</option>
                                        <option value="other">{t('venueEquipment:legalOther')}</option>
                                    </select>
                                </label>
                                <label>{t('venueEquipment:model')}<input value={kkmForm.modelName} onChange={e => setKkmForm({ ...kkmForm, modelName: e.target.value })} /></label>
                                <label>{t('venueEquipment:serialKkt')}<input value={kkmForm.serialNumber} onChange={e => setKkmForm({ ...kkmForm, serialNumber: e.target.value })} required /></label>
                            </>
                        )}
                        {kkmStep === 2 && (
                            <>
                                <label>{t('venueEquipment:fnsRegNumber')}<input value={kkmForm.fnsRegNumber} onChange={e => setKkmForm({ ...kkmForm, fnsRegNumber: e.target.value })} /></label>
                                <label>{t('venueEquipment:regDate')}<input type="date" value={kkmForm.fnsRegDate} onChange={e => setKkmForm({ ...kkmForm, fnsRegDate: e.target.value })} /></label>
                                <label>{t('venueEquipment:ffd')}
                                    <select value={kkmForm.ffdVersion} onChange={e => setKkmForm({ ...kkmForm, ffdVersion: e.target.value })}>
                                        <option value="1.05">1.05</option>
                                        <option value="1.1">1.1</option>
                                        <option value="1.2">1.2</option>
                                    </select>
                                </label>
                                <label><input type="checkbox" checked={kkmForm.isForServices} onChange={e => setKkmForm({ ...kkmForm, isForServices: e.target.checked })} /> {t('venueEquipment:forServices')}</label>
                            </>
                        )}
                        {kkmStep === 3 && (
                            <>
                                <label>{t('venueEquipment:fnModel')}
                                    <select value={kkmForm.fnModel} onChange={e => setKkmForm({ ...kkmForm, fnModel: e.target.value })}>
                                        {['ФН-1.1 15', 'ФН-1.1 36', 'ФН-1.2 15', 'ФН-1.2 36', 'ФН-2.0 36'].map(m => <option key={m}>{m}</option>)}
                                    </select>
                                </label>
                                <label>{t('venueEquipment:fnSerial')}<input value={kkmForm.fnSerialNumber} onChange={e => setKkmForm({ ...kkmForm, fnSerialNumber: e.target.value })} /></label>
                                <label>{t('venueEquipment:fnMonths')}
                                    <select value={kkmForm.fnValidityMonths} onChange={e => setKkmForm({ ...kkmForm, fnValidityMonths: parseInt(e.target.value, 10) })}>
                                        {[13, 15, 18, 36].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </label>
                                <label>{t('venueEquipment:activation')}<input type="date" value={kkmForm.fnActivationDate} onChange={e => {
                                    const v = e.target.value;
                                    setKkmForm({ ...kkmForm, fnActivationDate: v, fnExpirationDate: addMonths(v, kkmForm.fnValidityMonths) });
                                }} /></label>
                                <label>{t('venueEquipment:expiration')}<input type="date" value={kkmForm.fnExpirationDate} onChange={e => setKkmForm({ ...kkmForm, fnExpirationDate: e.target.value })} /></label>
                            </>
                        )}
                        {kkmStep === 4 && (
                            <>
                                <label>{t('venueEquipment:connectionType')}
                                    <select value={kkmForm.connectionMode} onChange={e => setKkmForm({ ...kkmForm, connectionMode: e.target.value })}>
                                        <option value="cloud">{t('venueEquipment:cloud')}</option>
                                        <option value="com">COM</option>
                                        <option value="usb_com">USB-COM</option>
                                        <option value="tcp_ip">TCP/IP</option>
                                        <option value="manual">{t('venueEquipment:manual')}</option>
                                    </select>
                                </label>
                                {kkmForm.connectionMode === 'cloud' && (
                                    <>
                                        <label>{t('venueEquipment:login')}<input value={kkmForm.cloudLogin} onChange={e => setKkmForm({ ...kkmForm, cloudLogin: e.target.value })} /></label>
                                        <label>{t('venueEquipment:password')}<input type="password" value={kkmForm.cloudPassword} onChange={e => setKkmForm({ ...kkmForm, cloudPassword: e.target.value })} /></label>
                                        <label>{t('venueEquipment:groupCode')}<input value={kkmForm.cloudGroupCode} onChange={e => setKkmForm({ ...kkmForm, cloudGroupCode: e.target.value })} /></label>
                                    </>
                                )}
                                {(kkmForm.connectionMode === 'com' || kkmForm.connectionMode === 'usb_com') && (
                                    <>
                                        {agent.alive && (
                                            <button type="button" onClick={loadPorts}>{t('venueEquipment:discoverDevices')}</button>
                                        )}
                                        <label>COM
                                            <select value={kkmForm.comPort} onChange={e => setKkmForm({ ...kkmForm, comPort: e.target.value })}>
                                                <option value="">—</option>
                                                {ports.map(p => <option key={p.port} value={p.port}>{p.port} — {p.description}</option>)}
                                            </select>
                                        </label>
                                        <label>{t('venueEquipment:speed')}
                                            <select value={kkmForm.comBaudRate} onChange={e => setKkmForm({ ...kkmForm, comBaudRate: parseInt(e.target.value, 10) })}>
                                                {[9600, 19200, 38400, 57600, 115200].map(b => <option key={b} value={b}>{b}</option>)}
                                            </select>
                                        </label>
                                    </>
                                )}
                                {kkmForm.connectionMode === 'tcp_ip' && (
                                    <>
                                        <label>IP<input value={kkmForm.tcpIpAddress} onChange={e => setKkmForm({ ...kkmForm, tcpIpAddress: e.target.value })} /></label>
                                        <label>{t('venueEquipment:port')}<input type="number" value={kkmForm.tcpPort} onChange={e => setKkmForm({ ...kkmForm, tcpPort: parseInt(e.target.value, 10) })} /></label>
                                        {agent.alive && (
                                            <button type="button" onClick={async () => {
                                                try {
                                                    const found = await scanAgentNetwork('192.168.1');
                                                    if (found[0]) setKkmForm(f => ({ ...f, tcpIpAddress: found[0].ip, tcpPort: found[0].port }));
                                                } catch { setError(t('venueEquipment:errScanNetwork')); }
                                            }}>{t('venueEquipment:scanNetwork')}</button>
                                        )}
                                    </>
                                )}
                                {kkmForm.connectionMode === 'manual' && (
                                    <p style={{ fontSize: 13, color: '#64748b', margin: '8px 0' }}>
                                        {t('venueEquipment:manualHint')}
                                    </p>
                                )}
                            </>
                        )}
                        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                            {kkmStep > 1 && <button type="button" onClick={() => setKkmStep(s => s - 1)}>{t('common:back')}</button>}
                            {kkmStep < 4 ? (
                                <button type="button" onClick={() => setKkmStep(s => s + 1)}>{t('common:next')}</button>
                            ) : (
                                <button type="submit" disabled={busy === 'kkm'}>{busy === 'kkm' ? '…' : t('common:save')}</button>
                            )}
                            <button type="button" onClick={() => setKkmModal(null)}>{t('common:cancel')}</button>
                        </div>
                    </form>
                </div>
            )}

            {terminalModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <form onSubmit={handleSaveTerminal} style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 440, width: '95%' }}>
                        <h3>{t('venueEquipment:acquiringTerminal')}</h3>
                        <label>{t('venueEquipment:name')}<input value={terminalForm.name} onChange={e => setTerminalForm({ ...terminalForm, name: e.target.value })} required /></label>
                        <label>{t('venueEquipment:bank')}
                            <select value={terminalForm.bank} onChange={e => setTerminalForm({ ...terminalForm, bank: e.target.value })}>
                                <option value="sber">{t('venueEquipment:bankSber')}</option>
                                <option value="vtb">{t('venueEquipment:bankVtb')}</option>
                                <option value="tinkoff">{t('venueEquipment:bankTinkoff')}</option>
                                <option value="other">{t('venueEquipment:bankOther')}</option>
                            </select>
                        </label>
                        <label>TID<input value={terminalForm.tid} onChange={e => setTerminalForm({ ...terminalForm, tid: e.target.value })} /></label>
                        <label>{t('venueEquipment:model')}<input value={terminalForm.terminalModel} onChange={e => setTerminalForm({ ...terminalForm, terminalModel: e.target.value })} /></label>
                        <label>{t('venueEquipment:terminalType')}
                            <select value={terminalForm.integrationType} onChange={e => setTerminalForm({ ...terminalForm, integrationType: e.target.value, connectionMode: e.target.value === 'standalone' ? 'standalone' : 'com' })}>
                                <option value="standalone">{t('venueEquipment:standalone')}</option>
                                <option value="integrated">{t('venueEquipment:integrated')}</option>
                            </select>
                        </label>
                        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                            <button type="submit" disabled={busy === 'term'}>{t('common:save')}</button>
                            <button type="button" onClick={() => setTerminalModal(null)}>{t('common:cancel')}</button>
                        </div>
                    </form>
                </div>
            )}

            {venueId > 0 && <SkudVenueSettings venueId={venueId} />}
        </OwnerPageLayout>
    );
}
